import { useEffect, useRef } from 'react';

/**
 * Architecture Rule 74 — ringtone for the IncomingCallModal.
 *
 * Behaviour, in order of preference:
 *
 *   1. MP3 / audio file       → if `ringtoneUrl` is set OR `REACT_APP_RINGTONE_URL`
 *                                env var is non-empty, load the file via the
 *                                native HTMLAudioElement and loop it. This is the
 *                                preferred path because real ringtones are
 *                                louder, richer, and survive the autoplay-policy
 *                                trap better than synthesised tones.
 *
 *   2. Web Audio API synth   → fallback when no URL is configured. Synthesises
 *                                a classic two-tone "brrring brrring" pattern
 *                                (440Hz + 480Hz mixed, ~1.5s ring / 3s silence).
 *                                IP-clean — no MP3 ships in the bundle.
 *
 * Browser-quirk discipline (auto-play policy):
 *   Chromium / Safari refuse to play audio without prior user interaction.
 *   The IncomingCallModal is triggered by a WebSocket event (no user
 *   interaction in this tab), so the first ring on a freshly-loaded tab can
 *   be silently blocked. That's by design — we never throw on it. The
 *   browser's `<Notification>` API (separately wired in the modal) covers
 *   the always-fires path. Once the user clicks Answer/Decline, subsequent
 *   rings work because the AudioContext / <audio> is now "user-gestured".
 *
 * Usage:
 *   useRingtone(Boolean(incoming));                             // synth fallback
 *   useRingtone(Boolean(incoming), '/ringtone.mp3');            // explicit URL
 *
 *   // Or set REACT_APP_RINGTONE_URL=/ringtone.mp3 in .env to make the
 *   // file the default for every IncomingCallModal mount.
 */
export default function useRingtone(active, ringtoneUrl) {
  // Lazily resolve the URL at mount-time so a swap to a different env value
  // between mounts is honoured (Storybook / CRA fast-refresh ergonomics).
  const resolvedUrl =
    typeof ringtoneUrl === 'string' && ringtoneUrl.length > 0
      ? ringtoneUrl
      : (process.env.REACT_APP_RINGTONE_URL || '');

  const ctxRef    = useRef(null);
  const audioRef  = useRef(null);
  const timerRef  = useRef(null);
  const gainRef   = useRef(null);

  useEffect(() => {
    if (!active) return undefined;
    if (typeof window === 'undefined') return undefined;

    // ── Path 1: native MP3 / audio file ────────────────────────────────────
    if (resolvedUrl) {
      const audio = new Audio(resolvedUrl);
      audio.loop = true;
      audio.preload = 'auto';
      // Modest default volume — modal is a foreground UI, not an emergency.
      audio.volume = 0.55;
      audioRef.current = audio;

      // play() returns a promise that rejects when the autoplay policy blocks
      // it. We swallow the rejection silently — the Notification API covers
      // the always-fires path and the next user-gesture will unblock it.
      const playPromise = audio.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => { /* autoplay blocked — silent fail intended */ });
      }

      return () => {
        try {
          audio.pause();
          audio.currentTime = 0;
        } catch (_) { /* element already torn down */ }
        // Help GC by detaching the src so the browser releases the buffer.
        try { audio.src = ''; audio.load(); } catch (_) { /* idem */ }
        audioRef.current = null;
      };
    }

    // ── Path 2: Web Audio API synth (no MP3 configured) ────────────────────
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return undefined;

    const ctx = new AudioCtx();
    ctxRef.current = ctx;
    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => { /* auto-play policy — silent fail is intended */ });
    }

    const masterGain = ctx.createGain();
    masterGain.gain.value = 0.0;
    masterGain.connect(ctx.destination);
    gainRef.current = masterGain;

    const oscA = ctx.createOscillator();
    const oscB = ctx.createOscillator();
    oscA.type = 'sine';  oscA.frequency.value = 440;
    oscB.type = 'sine';  oscB.frequency.value = 480;
    oscA.connect(masterGain);
    oscB.connect(masterGain);
    oscA.start();
    oscB.start();

    const RING_MS    = 1500;
    const SILENCE_MS = 3000;
    const PEAK_GAIN  = 0.18;

    const ringOnce = () => {
      const now = ctx.currentTime;
      masterGain.gain.cancelScheduledValues(now);
      masterGain.gain.setValueAtTime(0, now);
      masterGain.gain.linearRampToValueAtTime(PEAK_GAIN, now + 0.05);
      masterGain.gain.setValueAtTime(PEAK_GAIN, now + (RING_MS / 1000) - 0.05);
      masterGain.gain.linearRampToValueAtTime(0, now + (RING_MS / 1000));
    };

    ringOnce();
    timerRef.current = setInterval(ringOnce, RING_MS + SILENCE_MS);

    return () => {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      try { oscA.stop(); oscB.stop(); } catch (_) { /* already stopped */ }
      try { masterGain.disconnect(); } catch (_) { /* idem */ }
      try { ctx.close(); } catch (_) { /* idem */ }
      ctxRef.current = null;
      gainRef.current = null;
    };
  }, [active, resolvedUrl]);
}
